import React, { useEffect, useState } from "react";
import { Row, Col, Label, Input, Button } from "reactstrap";
import api from "../../../../api";   // your axios instance

const EcnPropertiesTab = ({ data, update }) => {

    const [users, setUsers] = useState([]);
    // Hotfix (post-R10) — separate state for the Document Manager dropdown.
    // Loaded from GET /api/dms/managers (RoleId=8 only, filtered by the
    // user's country). The Approver and Notification dropdowns continue to
    // use the full `users` list since those can legitimately be anyone.
    const [managers, setManagers] = useState([]);
    const [managersLoading, setManagersLoading] = useState(true);
    const [selectedApprover, setSelectedApprover] = useState("");
    const [selectedNotification, setSelectedNotification] = useState("");

    // Round DMS-UAT-6 — layered name resolution ("still I am seeing empid
    // only" UAT report). The dropdown roster (/WhosInBuilding/userList)
    // only contains ACTIVE, SSW-company employees, so terminated staff and
    // Shukla/other-company employees on legacy documents resolved to bare
    // EmpIDs. Resolution order:
    //   1. the active roster (users);
    //   2. data.knownNames — names the /full endpoint already resolved
    //      against the FULL EmpBasic table (all companies/statuses),
    //      carried through by CreateEcn.loadDocument;
    //   3. null → the row renders "ID: #### (name not on file)".
    const resolveName = (empId) => {
        const fromRoster = users.find(u => String(u.empId) === String(empId));
        if (fromRoster?.name) return fromRoster.name;
        const fromKnown = data.knownNames?.[String(empId)];
        if (fromKnown) return fromKnown;
        return null;
    };

    // Load employees once
    useEffect(() => {
        const loadUsers = async () => {
            // Go-live parity (2026-09-24): DMS#18 — the author / approver /
            // notification pickers use the DMS roster (active indirect-labor
            // employees of every company, alphabetical — legacy DMSNew.vb:73).
            // The shared Who's-In-Building list (SSW company only, newest hire
            // first) stays as a fallback so the pickers never come up empty.
            try {
                const response = await api.get("/dms/employees");
                if (Array.isArray(response.data) && response.data.length > 0) {
                    setUsers(response.data);
                    return;
                }
            } catch (err) {
                console.error("Failed to load DMS employee roster", err);
            }
            try {
                const response = await api.get("/WhosInBuilding/userList");
                setUsers(response.data);
            } catch (err) {
                console.error("Failed to load users", err);
            }
        };

        loadUsers();
    }, []);

    // Hotfix (post-R10) — Load DMS Managers (RoleId=8) separately for the
    // Document Manager dropdown. Country comes from the JWT session — same
    // place R7's CreateEcn auto-seed reads it from.
    useEffect(() => {
        const loadManagers = async () => {
            setManagersLoading(true);
            try {
                let country = "USA";
                try {
                    const auth = JSON.parse(sessionStorage.getItem("authUser") || "{}");
                    country = auth.countryCode || auth.country || "USA";
                } catch { /* fall back */ }

                const response = await api.get("/dms/managers", { params: { country } });
                setManagers(response.data || []);
            } catch (err) {
                console.error("Failed to load DMS managers list", err);
                setManagers([]);
            } finally {
                setManagersLoading(false);
            }
        };
        loadManagers();
    }, []);

    // Add approver
    const addApprover = () => {
        if (!selectedApprover) return;
        // ✅ stringify both sides to avoid type mismatch
        if (!data.approvers.some(id => String(id) === String(selectedApprover))) {
            update("approvers", [...data.approvers, selectedApprover]);
        }
        setSelectedApprover("");
    };

    // Remove approver
    const removeApprover = (empId) => {
        update(
            "approvers",
            data.approvers.filter(id => id !== empId)
        );
    };



    const addNotification = () => {
        if (!selectedNotification) return;
        // ✅ stringify both sides
        if (!data.notificationList.some(id => String(id) === String(selectedNotification))) {
            update("notificationList", [...data.notificationList, selectedNotification]);
        }
        setSelectedNotification("");
    };

    const removeNotification = (empId) => {
        update(
            "notificationList",
            data.notificationList.filter(id => id !== empId)
        );
    };
    return (
        <>
            {/* Author + Manager */}
            <Row className="mb-4">
                <Col md={6}>
                    <Label>Document Author</Label>
                    {/* Round DMS-2 (owner request #5): DOCUMENT CONTROL can
                        change the author at ANY stage — the picker unlocks
                        for the isItAdmin / isDmsManager claims (the server
                        independently re-checks CallerIsDocumentControl and
                        rev-logs every change, so this is UX gating only;
                        release-module-only Document Control users can still
                        change it via the API/server even if the picker shows
                        locked for them). Everyone else: read-only as before. */}
                    <Input
                        type="select"
                        value={data.authorId || ""}
                        disabled={!(() => {
                            try {
                                const u = JSON.parse(sessionStorage.getItem("authUser") || "{}");
                                return Boolean(u.isItAdmin || u.isDmsManager);
                            } catch { return false; }
                        })()}
                        onChange={(e) => update("authorId", e.target.value)}
                    >
                        <option value="">Select Author</option>
                        {/* Round DMS-UAT-6 — if the document's author isn't in
                            the active-SSW roster (terminated / other company),
                            inject a synthetic option so the field doesn't
                            render blank. Uses the /full-resolved name. */}
                        {data.authorId
                            && !users.some(u => String(u.empId) === String(data.authorId)) && (
                            <option value={data.authorId}>
                                {resolveName(data.authorId) || `ID: ${data.authorId}`} ({data.authorId})
                            </option>
                        )}
                        {users.map(u => (
                            <option key={u.empId} value={u.empId}>
                                {u.name} ({u.empId})
                            </option>
                        ))}
                    </Input>
                </Col>

                <Col md={6}>
                    <Label>Document Manager</Label>
                    {/* Hotfix (post-R10) — was `disabled` with `users.map`
                        (all employees). Now uses the RoleId=8-filtered
                        `managers` list from GET /api/dms/managers and is
                        actually pickable. */}
                    <Input
                        type="select"
                        value={data.managerId || ""}
                        onChange={(e) => update("managerId", e.target.value)}
                        disabled={managersLoading}
                    >
                        <option value="">
                            {managersLoading
                                ? "Loading managers…"
                                : managers.length === 0
                                    ? "No DMS Managers configured for your country"
                                    : "Select Manager"}
                        </option>
                        {/* Round DMS-UAT-6 — synthetic option for a current
                            manager who isn't in the RoleId=8 list (legacy
                            data), so the field shows who it is instead of
                            rendering blank. */}
                        {data.managerId
                            && !managers.some(m => String(m.empId) === String(data.managerId)) && (
                            <option value={data.managerId}>
                                {resolveName(data.managerId) || `ID: ${data.managerId}`} ({data.managerId}) — current
                            </option>
                        )}
                        {managers.map(m => (
                            <option key={m.empId} value={m.empId}>
                                {m.name} ({m.empId})
                            </option>
                        ))}
                    </Input>
                </Col>
            </Row>

            <h6>Electronic Notification List</h6>

            <Row className="mb-3">
                <Col md={8}>
                    <Input
                        type="select"
                        value={selectedNotification}
                        onChange={(e) =>
                            setSelectedNotification(e.target.value)
                        }
                    >
                        <option value="">Select Employee</option>
                        {users.map(u => (
                            <option key={u.empId} value={u.empId}>
                                {u.name} ({u.empId})
                            </option>
                        ))}
                    </Input>
                </Col>

                <Col md={4}>
                    <Button color="primary" onClick={addNotification}>
                        Add
                    </Button>
                </Col>
            </Row>

            <div className="border p-3 mb-4">
                {data.notificationList.length === 0 && (
                    <p className="text-muted">No employees added.</p>
                )}

                {data.notificationList.map(empId => {
                    const user = users.find(u =>
                        String(u.empId) === String(empId)  // ✅ stringify both
                    );
                    return (
                        <div
                            key={empId}
                            className="d-flex justify-content-between align-items-center mb-2"
                        >
                            <span>{resolveName(empId)
                                ? <>{resolveName(empId)} <span className="text-muted">(ID: {empId})</span></>
                                : <>ID: {empId} <span className="text-muted">(name not on file)</span></>}</span>
                            <Button
                                size="sm"
                                color="danger"
                                onClick={() => removeNotification(empId)}
                            >
                                Remove
                            </Button>
                        </div>
                    );
                })}
            </div>


            {/* Approvers */}
            <h6>Approval List</h6>

            <Row className="mb-3">
                <Col md={8}>
                    <Input
                        type="select"
                        value={selectedApprover}
                        onChange={(e) =>
                            setSelectedApprover(e.target.value)
                        }
                    >
                        <option value="">Select Approver</option>
                        {users.map(u => (
                            <option key={u.empId} value={u.empId}>
                                {u.name} ({u.empId})
                            </option>
                        ))}
                    </Input>
                </Col>

                <Col md={4}>
                    <Button color="primary" onClick={addApprover}>
                        Add Approver
                    </Button>
                </Col>
            </Row>

            <div className="border p-3">
                {data.approvers.length === 0 && (
                    <p className="text-muted">No approvers added.</p>
                )}

                {data.approvers.map(empId => {
                    // Go-live parity (2026-09-24): no hard-coded EmpID — every
                    // approver (the auto-seeded default included) is removable,
                    // as in legacy (DMSNew.vb:198-205 pre-added but removable).
                    return (
                        <div
                            key={empId}
                            className="d-flex justify-content-between align-items-center mb-2"
                        >
                            <span>{resolveName(empId)
                                ? <>{resolveName(empId)} <span className="text-muted">(ID: {empId})</span></>
                                : <>ID: {empId} <span className="text-muted">(name not on file)</span></>}</span>
                            <Button
                                size="sm"
                                color="danger"
                                onClick={() => removeApprover(empId)}
                            >
                                Remove
                            </Button>
                        </div>
                    );
                })}
            </div>
        </>
    );
};

export default EcnPropertiesTab;